import React, { useEffect, useRef, useState } from "react";
import { Loader2, Trash2, Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import PhotoAvatar from "./PhotoAvatar";
import { SourceBadge, TopPerformerBadge } from "./RealtorBadges";
import InteractionTimeline from "./InteractionTimeline";
import OutreachLogPanel from "./OutreachLogPanel";
import BrokeragePeekModal from "@/components/brokerages/BrokeragePeekModal";

// Realtor edit dialog and the shared RealtorForm used by both add + edit.
// The manual-add dialog lives in ./AddRealtorManuallyDialog.jsx so its
// vCard-import logic stays isolated from the (much larger) profile edit
// flow.

export function blankRealtor() {
  return {
    first_name: "",
    last_name: "",
    brokerage_id: "",
    brokerage_name: "",
    title: "",
    nickname: "",
    business_name: "",
    city: "",
    phone: "",
    work_phone: "",
    email: "",
    photo_url: "",
    profile_url: "",
    realtor_website_url: "",
    facebook_url: "",
    instagram_url: "",
    linkedin_url: "",
    tiktok_url: "",
    threads_url: "",
    twitter_url: "",
    youtube_url: "",
    birthday: "",
    notes: "",
    tags: [],
    source: "manual",
  };
}

export function RealtorEditDialog({
  realtor,
  brokerages,
  locations = [],
  isAdmin = false,
  onClose,
  onDone,
  onPatch,
  scrollTo = null,
}) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const outreachRef = useRef(null);
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    setR(realtor ? { ...realtor } : null);
  }, [realtor]);
  // Honour deep-link section scrolling. `?section=outreach` on the URL
  // opens the profile scrolled straight to the outreach timeline so the
  // user doesn't have to page past the edit form on mobile.
  useEffect(() => {
    if (!realtor || scrollTo !== "outreach") return;
    // Wait for the dialog + form to paint before scrolling, else the
    // outreach ref hasn't attached yet.
    const t = setTimeout(() => {
      const target = outreachRef.current;
      const container = scrollContainerRef.current;
      if (!target) return;
      if (container) {
        container.scrollTo({
          top: target.offsetTop - 12,
          behavior: "smooth",
        });
      } else {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 120);
    return () => clearTimeout(t);
  }, [realtor, scrollTo]);
  if (!realtor || !r) return null;
  // Auto-save any pending in-dialog edits (notes, tags, phone, etc.) before
  // firing a timeline action. Called by OutreachLogPanel + InteractionTimeline
  // before their own POSTs so a user can type into the Notes textarea and
  // then tap "Mark as met in person" (or Call / SMS / Referral) without
  // losing their draft. Fields we auto-save match the explicit Save button's
  // patch shape. No-op if nothing has changed.
  const flushDirtyEdits = async () => {
    if (!r || !realtor) return;
    const dirty = {};
    const FIELDS = [
      "first_name", "last_name", "title", "nickname", "business_name",
      "city",
      "phone", "work_phone", "email",
      "photo_url", "profile_url", "realtor_website_url",
      "facebook_url", "instagram_url", "linkedin_url", "tiktok_url",
      "threads_url", "twitter_url", "youtube_url",
      "birthday", "notes",
    ];
    for (const f of FIELDS) {
      if ((r[f] || "") !== (realtor[f] || "")) dirty[f] = r[f] ?? "";
    }
    // tags is a list — compare shallow-equal
    const oldTags = Array.isArray(realtor.tags) ? realtor.tags : [];
    const newTags = Array.isArray(r.tags) ? r.tags : [];
    if (oldTags.length !== newTags.length || oldTags.some((t, i) => t !== newTags[i])) {
      dirty.tags = newTags;
    }
    if (Object.keys(dirty).length === 0) return;
    try {
      await api.patch(`/realtors/${realtor.id}`, dirty);
      // Sync the parent row in place WITHOUT closing the dialog (onPatch,
      // not onDone) so typing a note then tapping "Met Today" keeps the
      // profile open.
      onPatch && onPatch({ id: realtor.id, ...dirty });
    } catch (err) {
      toast.error("Couldn't save profile edits", { description: formatApiError(err) });
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        first_name: r.first_name,
        last_name: r.last_name,
        brokerage_id: r.brokerage_id || "",
        title: r.title,
        nickname: r.nickname ?? "",
        business_name: r.business_name ?? "",
        city: r.city ?? "",
        phone: r.phone,
        work_phone: r.work_phone ?? "",
        email: r.email,
        photo_url: r.photo_url,
        profile_url: r.profile_url,
        realtor_website_url: r.realtor_website_url,
        facebook_url: r.facebook_url,
        instagram_url: r.instagram_url,
        linkedin_url: r.linkedin_url,
        tiktok_url: r.tiktok_url,
        threads_url: r.threads_url,
        twitter_url: r.twitter_url,
        youtube_url: r.youtube_url,
        birthday: r.birthday,
        notes: r.notes,
        tags: r.tags,
        ...(isAdmin && r.location_id ? { location_id: r.location_id, location_ids: r.location_ids || [r.location_id] } : {}),
      };
      await api.patch(`/realtors/${realtor.id}`, patch);
      toast.success("Saved");
      // Pass the patch back so the parent list can update the row in-place
      // (no full reload → scroll position and filters are preserved).
      onDone({ id: realtor.id, ...patch });
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/realtors/${realtor.id}`);
      toast.success(`Deleted ${realtor.name || `${realtor.first_name} ${realtor.last_name}`.trim()}`, {
        description: "Moved to Recycle Bin — you can restore it from there.",
      });
      setConfirmDelete(false);
      // Signal deletion to the parent so it can remove the row in-place.
      onDone && onDone({ id: realtor.id, _deleted: true });
      onClose && onClose();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    } finally {
      setDeleting(false);
    }
  };
  return (
    <Dialog open={!!realtor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl w-full h-[90vh] sm:h-auto sm:max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="edit-realtor-dialog"
        onOpenAutoFocus={(e) => {
          // Radix's default is to focus the first focusable element (the
          // first-name Input) — that pops the on-screen keyboard on
          // mobile immediately after opening, which the user asked us to
          // stop. Prevent the default; users can still tap into any
          // field to edit.
          e.preventDefault();
        }}
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-4 sm:px-6 py-3 sm:py-4">
          <DialogTitle className="flex items-center gap-3 pr-8">
            <PhotoAvatar realtor={r} />
            <div className="min-w-0">
              <div className="truncate flex items-center gap-2">
                <span className="truncate">
                  {r.name || `${r.first_name} ${r.last_name}`}
                </span>
                <SourceBadge source={r.source} />
                <TopPerformerBadge realtor={r} />
              </div>
              <div className="text-xs font-normal text-slate-500 truncate">
                {r.brokerage_name}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4"
        >
          <RealtorForm
            r={r}
            setR={setR}
            brokerages={brokerages}
            idPrefix="edit"
            locations={locations}
            showLocation={isAdmin && locations.length > 0}
          />
          <div ref={outreachRef}>
            <OutreachLogPanel
              realtor={r}
              flushDirtyEdits={flushDirtyEdits}
              onUpdated={(updated) => {
                setR(updated);
                // In-place row sync only — keep the dialog OPEN so a user
                // can log met/outreach without losing their place.
                onPatch && onPatch({ id: updated?.id || r.id, ...(updated || {}) });
              }}
            />
          </div>
          <InteractionTimeline
            realtor={r}
            flushDirtyEdits={flushDirtyEdits}
            onInteractionChange={(updated) => {
              // Timeline edits (call/sms/email/note/referral logged or
              // removed) bump the realtor's outreach_log / last_contacted
              // fields server-side. Patch the parent row in place (no close,
              // no full reload).
              if (updated) {
                setR((prev) => ({ ...prev, ...updated }));
                onPatch && onPatch({ id: updated.id || r.id, ...updated });
              }
            }}
          />
        </div>
        <DialogFooter className="shrink-0 border-t border-slate-200 bg-white px-4 sm:px-6 py-3 flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            className="min-h-[44px] border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
            data-testid="delete-realtor-button"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete profile
          </Button>
          <div className="flex gap-2 sm:justify-end">
            <Button variant="ghost" onClick={onClose} className="min-h-[44px]">
              Close
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              className="text-white min-h-[44px]"
              style={{ background: "hsl(var(--brand))" }}
              data-testid="save-realtor-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        </DialogFooter>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="delete-realtor-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 font-display">
                <Trash2 className="h-5 w-5 text-rose-600" />
                Delete {realtor.name || `${realtor.first_name} ${realtor.last_name}`.trim()}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This soft-deletes the profile and moves it to the <strong>Recycle Bin</strong>,
                where you can restore it later if needed. Their brokerage record and any
                open-house history stay intact.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} className="min-h-[44px]">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={deleting}
                onClick={(e) => {
                  e.preventDefault();
                  doDelete();
                }}
                className="bg-rose-600 hover:bg-rose-700 text-white min-h-[44px]"
                data-testid="delete-realtor-confirm"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete profile
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

export function RealtorForm({
  r,
  setR,
  brokerages,
  idPrefix,
  locations = [],
  showLocation = false,
}) {
  const [peekBrokerageId, setPeekBrokerageId] = useState(null);
  const tagsString = (r.tags || []).join(", ");
  const onTagsChange = (v) => {
    const arr = v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setR({ ...r, tags: arr });
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="First name" id={`${idPrefix}-fn`}>
        <Input
          value={r.first_name || ""}
          onChange={(e) => setR({ ...r, first_name: e.target.value })}
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-first-name`}
        />
      </Field>
      <Field label="Last name" id={`${idPrefix}-ln`}>
        <Input
          value={r.last_name || ""}
          onChange={(e) => setR({ ...r, last_name: e.target.value })}
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-last-name`}
        />
      </Field>
      <Field label="Brokerage" id={`${idPrefix}-bk`} className="sm:col-span-2">
        {/* Row splits into (Select | Peek button) so tapping the field
            still opens the change-brokerage dropdown as before, while
            the icon on the right jumps straight to the brokerage's
            detail sheet without leaving the realtor card. */}
        <div className="flex gap-2 items-stretch">
          <div className="flex-1 min-w-0">
            <Select
              value={r.brokerage_id || ""}
              onValueChange={(v) => setR({ ...r, brokerage_id: v })}
            >
              <SelectTrigger
                className="min-h-[44px]"
                data-testid={`${idPrefix}-realtor-brokerage`}
              >
                <SelectValue placeholder={r.brokerage_name || "Select brokerage"} />
              </SelectTrigger>
              <SelectContent>
                {brokerages
                  .filter((b) => b.status !== "rejected")
                  .map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          {r.brokerage_id ? (
            <button
              type="button"
              onClick={() => setPeekBrokerageId(r.brokerage_id)}
              className="shrink-0 inline-flex items-center justify-center min-h-[44px] w-11 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[hsl(var(--brand-strong))] hover:border-slate-300 transition-colors"
              aria-label="Open brokerage details"
              title="Open brokerage details"
              data-testid={`${idPrefix}-realtor-brokerage-open`}
            >
              <Building2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </Field>
      {peekBrokerageId ? (
        <BrokeragePeekModal
          brokerageId={peekBrokerageId}
          onClose={() => setPeekBrokerageId(null)}
        />
      ) : null}
      {showLocation ? (
        <LocationManager
          r={r}
          setR={setR}
          locations={locations}
          idPrefix={idPrefix}
        />
      ) : null}
      <Field label="Title" id={`${idPrefix}-title`}>
        <Input
          value={r.title || ""}
          onChange={(e) => setR({ ...r, title: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field label="Nickname" id={`${idPrefix}-nick`}>
        <Input
          value={r.nickname || ""}
          onChange={(e) => setR({ ...r, nickname: e.target.value })}
          className="min-h-[44px]"
          placeholder="Optional · e.g. Chris"
          data-testid={`${idPrefix}-realtor-nickname`}
        />
      </Field>
      <Field
        label="Business name"
        id={`${idPrefix}-biz`}
        className="sm:col-span-2"
      >
        <Input
          value={r.business_name || ""}
          onChange={(e) => setR({ ...r, business_name: e.target.value })}
          className="min-h-[44px]"
          placeholder="Optional · e.g. Real Estate Prof. Corp."
          data-testid={`${idPrefix}-realtor-business-name`}
        />
      </Field>
      <Field label="Address — City/Town" id={`${idPrefix}-city`} className="sm:col-span-2">
        <Input
          value={r.city || ""}
          onChange={(e) => setR({ ...r, city: e.target.value })}
          className="min-h-[44px]"
          placeholder="Town/municipality this agent is based in · e.g. Red Deer"
          data-testid={`${idPrefix}-realtor-city`}
        />
      </Field>
      <Field label="Phone" id={`${idPrefix}-phone`}>
        <Input
          value={r.phone || ""}
          onChange={(e) => setR({ ...r, phone: e.target.value })}
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-phone`}
        />
      </Field>
      <Field label="Work phone (brokerage / office)" id={`${idPrefix}-work-phone`}>
        <Input
          value={r.work_phone || ""}
          onChange={(e) => setR({ ...r, work_phone: e.target.value })}
          className="min-h-[44px]"
          placeholder="Brokerage main line"
          data-testid={`${idPrefix}-realtor-work-phone`}
        />
      </Field>
      <Field label="Email" id={`${idPrefix}-email`}>
        <Input
          type="email"
          value={r.email || ""}
          onChange={(e) => setR({ ...r, email: e.target.value })}
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-email`}
        />
      </Field>
      <Field label="Birthday (YYYY-MM-DD)" id={`${idPrefix}-bday`}>
        <Input
          value={r.birthday || ""}
          onChange={(e) => setR({ ...r, birthday: e.target.value })}
          className="min-h-[44px]"
          placeholder="1985-06-15"
        />
      </Field>
      <Field label="Photo URL" id={`${idPrefix}-photo`} className="sm:col-span-2">
        <Input
          value={r.photo_url || ""}
          onChange={(e) => setR({ ...r, photo_url: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field
        label="Profile URL"
        id={`${idPrefix}-profile`}
        className="sm:col-span-2"
      >
        <Input
          value={r.profile_url || ""}
          onChange={(e) => setR({ ...r, profile_url: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field label="Facebook URL" id={`${idPrefix}-fb`}>
        <Input
          value={r.facebook_url || ""}
          onChange={(e) => setR({ ...r, facebook_url: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field label="Instagram URL" id={`${idPrefix}-ig`}>
        <Input
          value={r.instagram_url || ""}
          onChange={(e) => setR({ ...r, instagram_url: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field label="LinkedIn URL" id={`${idPrefix}-li`}>
        <Input
          value={r.linkedin_url || ""}
          onChange={(e) => setR({ ...r, linkedin_url: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field label="TikTok URL" id={`${idPrefix}-tt`}>
        <Input
          value={r.tiktok_url || ""}
          onChange={(e) => setR({ ...r, tiktok_url: e.target.value })}
          className="min-h-[44px]"
        />
      </Field>
      <Field label="Twitter / X URL" id={`${idPrefix}-tw`}>
        <Input
          value={r.twitter_url || ""}
          onChange={(e) => setR({ ...r, twitter_url: e.target.value })}
          placeholder="https://twitter.com/handle"
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-twitter`}
        />
      </Field>
      <Field label="YouTube URL" id={`${idPrefix}-yt`}>
        <Input
          value={r.youtube_url || ""}
          onChange={(e) => setR({ ...r, youtube_url: e.target.value })}
          placeholder="https://youtube.com/@channel"
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-youtube`}
        />
      </Field>
      <Field label="Threads URL" id={`${idPrefix}-th`}>
        <Input
          value={r.threads_url || ""}
          onChange={(e) => setR({ ...r, threads_url: e.target.value })}
          placeholder="https://threads.net/@handle"
          className="min-h-[44px]"
          data-testid={`${idPrefix}-realtor-threads`}
        />
      </Field>
      <Field
        label="Tags (comma-separated)"
        id={`${idPrefix}-tags`}
        className="sm:col-span-2"
      >
        <Input
          value={tagsString}
          onChange={(e) => onTagsChange(e.target.value)}
          className="min-h-[44px]"
          placeholder="hot lead, repeat client"
        />
      </Field>
      <Field
        label="Notes (markdown ok)"
        id={`${idPrefix}-notes`}
        className="sm:col-span-2"
      >
        <Textarea
          value={r.notes || ""}
          onChange={(e) => setR({ ...r, notes: e.target.value })}
          rows={4}
          data-testid={`${idPrefix}-realtor-notes`}
        />
      </Field>
    </div>
  );
}

function LocationManager({ r, setR, locations, idPrefix }) {
  const [removeTarget, setRemoveTarget] = useState(null);
  const locIds = r.location_ids || (r.location_id ? [r.location_id] : []);
  const assignedLocs = locations.filter((l) => locIds.includes(l.id));
  const availableLocs = locations.filter((l) => !locIds.includes(l.id));

  const addLocation = (locId) => {
    if (!locId) return;
    const next = [...locIds, locId];
    setR({ ...r, location_ids: next, location_id: next[0] || "" });
  };

  const confirmRemove = () => {
    if (!removeTarget) return;
    const next = locIds.filter((id) => id !== removeTarget);
    setR({ ...r, location_ids: next, location_id: next[0] || "" });
    setRemoveTarget(null);
  };

  const removeLocName = locations.find((l) => l.id === removeTarget)?.name || "this location";

  return (
    <Field label="Locations" id={`${idPrefix}-loc`} className="sm:col-span-2">
      <div className="space-y-1.5">
        {assignedLocs.map((loc, idx) => {
          const isLast = assignedLocs.length === 1;
          return (
            <div
              key={loc.id}
              className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 bg-white min-h-[44px]"
              data-testid={`${idPrefix}-realtor-location-${loc.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm text-slate-800 truncate">{loc.name}</span>
                {idx === 0 && assignedLocs.length > 1 && (
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium shrink-0">Primary</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setRemoveTarget(loc.id)}
                disabled={isLast}
                className={cn(
                  "shrink-0 p-1.5 rounded transition-colors",
                  isLast
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                )}
                aria-label={isLast ? "Cannot remove last location" : `Remove ${loc.name}`}
                title={isLast ? "Every realtor must have at least 1 location" : `Remove ${loc.name}`}
                data-testid={`${idPrefix}-realtor-location-remove-${loc.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {availableLocs.length > 0 && (
        <div className="mt-2">
          <Select onValueChange={addLocation} value="">
            <SelectTrigger
              className="min-h-[44px] border-dashed"
              data-testid={`${idPrefix}-realtor-add-location`}
            >
              <span className="flex items-center gap-1.5 text-slate-500">
                <Plus className="h-4 w-4" />
                Add Area
              </span>
            </SelectTrigger>
            <SelectContent>
              {availableLocs.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {assignedLocs.length > 1 && (
        <p className="text-xs text-slate-500 mt-1.5">
          This realtor appears in all {assignedLocs.length} locations above. Primary: {assignedLocs[0]?.name || "—"}.
        </p>
      )}

      {/* Confirmation dialog for removing a location */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent data-testid={`${idPrefix}-remove-location-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from {removeLocName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This realtor's profile will no longer be viewable in <strong>{removeLocName}</strong>.
              You can re-add it later using the "Add Area" button.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRemove(); }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid={`${idPrefix}-remove-location-confirm-action`}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Field>
  );
}

function Field({ label, id, children, className = "" }) {
  return (
    <div className={className}>
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-wide text-slate-500"
      >
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
